import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { morphProductLockups } from './core/utils/product-view-transition';
import { DEFAULT_LANG } from './core/data/languages.config';
import { AnalyticsService } from './core/services/analytics.service';
import { LanguageService } from './core/services/language.service';
import { RegionService } from './core/services/region.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      // Route params arrive as component inputs: a guide page's :page, a legal document's :doc.
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),

      // Carries a product's lockup between the home page's cards and the
      // header's links. Scoped to that one move by `morphProductLockups`, so
      // the rest of the site navigates exactly as it did before.
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: morphProductLockups,
      }),
    ),
    provideHttpClient(withFetch()),

    // No transfer cache: it would serialize the whole dictionary into every page's HTML.
    provideClientHydration(withNoHttpTransferCache()),

    // The loader has to be passed inside provideTranslateService: registering
    // it separately is overridden by the default no-op loader. The leading
    // slash on the prefix matters too — without it the fetch would resolve
    // relative to the current route.
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
      fallbackLang: DEFAULT_LANG,
    }),

    // Resolve and load the language before the first render, so nothing ever
    // paints against a missing dictionary.
    provideAppInitializer(() => inject(LanguageService).init()),

    // country.is has no SLA; detection runs in the background and the
    // platform default is already a usable fallback the moment the page paints.
    provideAppInitializer(() => {
      inject(RegionService).detect();
    }),

    // Browser-only and consent-gated: the facade checks both itself and loads nothing before Accept.
    provideAppInitializer(() => {
      inject(AnalyticsService).start();
    }),
  ],
};
